import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ArticleCard } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { useReducedMotion } from '@/lib/hooks';
import { formatNumber } from '@/lib/format';
import { ArticleImage } from '@/components/content';
import { Icon } from '@/components/shell';
import { cn } from '@/components/ui';

/**
 * The hero carousel and the ranked list beside it — PRD §6 and §7, and the two are one
 * component because §7's requirement binds them: "selecting a ranked item should update
 * the hero story."
 *
 * That requirement decides the deck. The supplied design draws both lists from the same
 * five stories, but the CMS curates them separately (`hero_stories` and
 * `featured_stories` with placement `ranked`), so an editor can easily rank a story that
 * is not a hero slide. The carousel therefore shows **hero ∪ ranked**, hero first: every
 * ranked item can then become the hero, which is what §7 asks for, and the hero order an
 * editor set still leads.
 *
 * Every input method the task list names is wired, and each needed its own decision:
 *
 * - **Auto-rotation** runs at the duration an editor set in the CMS
 *   (`homepage_sections.settings.rotationSeconds`), and stops entirely when
 *   `autoRotate` is false or the reader prefers reduced motion.
 * - **Pause on hover *and* focus**, like the ticker: a carousel that keeps moving while
 *   someone is reading a slide, or tabbing through its links, is the same WCAG 2.2.2
 *   failure.
 * - **Keyboard** arrows move slides when focus is inside the carousel — not globally,
 *   which would hijack a reader scrolling the page with the arrow keys.
 * - **Touch** swipes horizontally, with a 40px threshold so a vertical scroll that
 *   wanders sideways does not change the story.
 * - **`aria-live="polite"`** announces the new headline. The slides themselves are not
 *   individually focusable: only the visible one is in the tab order, so a keyboard
 *   reader does not tab through five hidden stories.
 */
export interface HeroSettings {
  autoRotate: boolean;
  rotationSeconds: number;
}

const DEFAULT_SETTINGS: HeroSettings = { autoRotate: true, rotationSeconds: 7 };
const SWIPE_THRESHOLD_PX = 40;

export function HeroCarousel({
  slides,
  ranked,
  settings = DEFAULT_SETTINGS,
  priority = true,
}: {
  slides: ArticleCard[];
  ranked: ArticleCard[];
  settings?: HeroSettings;
  /**
   * Whether this is the page's leading image. False when the top-story band is above it
   * — only one image on a page may be the LCP candidate, and two eager loads at the top
   * of the document compete for the same first packet.
   */
  priority?: boolean;
}) {
  const { t, locale } = useLocale();
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const deck = useMemo(() => {
    const seen = new Set(slides.map((article) => article.id));
    return [...slides, ...ranked.filter((article) => !seen.has(article.id))];
  }, [slides, ranked]);

  const count = deck.length;
  const go = useCallback(
    (next: number) => setIndex(() => (count === 0 ? 0 : (next + count) % count)),
    [count],
  );

  useEffect(() => {
    const rotating = settings.autoRotate && !reducedMotion && !paused && count > 1;
    if (!rotating) return undefined;
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % count),
      Math.max(3, settings.rotationSeconds) * 1000,
    );
    return () => clearInterval(timer);
  }, [settings.autoRotate, settings.rotationSeconds, reducedMotion, paused, count]);

  if (count === 0) return null;
  const slide = deck[index] ?? deck[0];
  if (!slide) return null;

  const pauseHandlers = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onFocus: () => setPaused(true),
    onBlur: () => setPaused(false),
  };

  return (
    <div className="bg-soft px-gutter py-5.5">
      <div className="grid overflow-hidden rounded-sm bg-white lg:grid-cols-hero">
        <section
          aria-roledescription="carousel"
          aria-label={t('section.featured')}
          className="relative min-h-[340px] overflow-hidden lg:min-h-[500px]"
          {...pauseHandlers}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              go(index + 1);
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              go(index - 1);
            }
          }}
          onTouchStart={(event) => {
            touchStartX.current = event.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(event) => {
            const start = touchStartX.current;
            const end = event.changedTouches[0]?.clientX;
            touchStartX.current = null;
            if (start === null || end === undefined) return;
            const delta = end - start;
            if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
            go(index + (delta < 0 ? 1 : -1));
          }}
        >
          <div className="absolute inset-0">
            <ArticleImage
              image={slide.image}
              alt={slide.headline}
              priority={priority}
              sizes="(max-width: 1080px) 100vw, 70vw"
              aspect="h-full"
              placeholderSeed={slide.category.slug}
              className="h-full w-full"
            />
          </div>
          {/* The design's left-to-right scrim, which is what makes white text legible. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-[linear-gradient(90deg,rgba(20,24,30,.78)_0%,rgba(20,24,30,.45)_45%,rgba(20,24,30,.05)_75%)]"
          />

          <div
            className="absolute inset-x-0 bottom-0 z-10 max-w-[430px] px-6 pb-20 lg:px-8 lg:pb-24"
            aria-live="polite"
            aria-atomic="true"
          >
            {/*
              "FEATURED", not "TOP STORY". The carousel used to call itself the top story
              because it was the first thing on the page; the lead now has a band of its
              own above it (`TopStoryBand`), and two things on one page claiming the same
              title tells a reader neither is true.
            */}
            <p className="mb-3 text-sm font-extrabold uppercase tracking-wider text-red">
              {t('section.featured')}
            </p>
            {/*
              Clamped, and a step smaller in Assamese. A Bengali-script headline runs
              roughly a third longer than its English counterpart at the same point size,
              and the supplied Assamese design drops the hero from 46px to 32px for
              exactly that reason — without the clamp, a six-line headline pushes the CTA
              out of the hero entirely.
            */}
            <h2 className="mb-4 line-clamp-3 text-3xl font-extrabold text-white lg:text-4xl [:lang(en)_&]:lg:text-hero">
              <Link to={slide.path} className="hover:underline">
                {slide.headline}
              </Link>
            </h2>
            {slide.summary && (
              <p className="mb-5 line-clamp-3 max-w-[390px] text-md text-[#e6e8ea]">
                {slide.summary}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to={slide.path}
                className="inline-flex items-center gap-2 rounded-md bg-red px-5 py-3 text-sm font-bold text-white transition hover:bg-red-dark"
              >
                {t('action.readFullStory')}
                <Icon name="arrow-right" size={14} />
              </Link>
              <span className="text-sm font-semibold text-[#dfe2e5]">
                {t('meta.readTime', { minutes: slide.readingMinutes })}
              </span>
            </div>
          </div>

          <div className="absolute bottom-4 left-6 z-20 flex items-center gap-4 lg:left-8">
            <p className="flex items-center gap-3 text-sm font-bold text-[#cfd2d6]">
              <span>{formatNumber(index + 1, locale)}</span>
              <span className="relative block h-0.5 w-20 rounded-sm bg-white/30 sm:w-28">
                <span
                  className="absolute inset-y-0 left-0 rounded-sm bg-white transition-[width] duration-300"
                  style={{ width: `${((index + 1) / count) * 100}%` }}
                />
              </span>
              <span>{formatNumber(count, locale)}</span>
            </p>
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label={t('action.previousSlide')}
              className="grid h-8 w-8 place-items-center rounded-full border border-white/50 text-white transition hover:bg-white/15"
            >
              <Icon name="chevron-left" size={15} />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label={t('action.nextSlide')}
              className="grid h-8 w-8 place-items-center rounded-full border border-red bg-red text-white transition hover:bg-red-dark"
            >
              <Icon name="chevron-right" size={15} />
            </button>
          </div>
        </section>

        <RankedList
          articles={ranked}
          activeId={slide.id}
          onSelect={(articleId) => {
            const position = deck.findIndex((candidate) => candidate.id === articleId);
            if (position >= 0) setIndex(position);
          }}
        />
      </div>
    </div>
  );
}

/**
 * PRD §7's ranked column. Each row is a button when the story is also a hero slide — it
 * switches the carousel — and a plain link when it is not, because a control that
 * silently does nothing is worse than a link that goes somewhere.
 */
function RankedList({
  articles,
  activeId,
  onSelect,
}: {
  articles: ArticleCard[];
  activeId: number;
  onSelect: (articleId: number) => void;
}) {
  const { locale } = useLocale();

  if (articles.length === 0) return null;

  return (
    <ol className="hidden flex-col bg-white px-5 py-6 lg:flex">
      {articles.map((article, position) => {
        const active = article.id === activeId;
        return (
          <li key={article.id} className="border-b border-line last:border-b-0">
            <button
              type="button"
              onClick={() => onSelect(article.id)}
              aria-current={active ? 'true' : undefined}
              className="group grid w-full grid-cols-[34px_1fr] gap-2 py-3.5 text-left"
            >
              <span
                className={cn(
                  'font-serif text-3xl font-extrabold',
                  active ? 'text-red' : 'text-[#c8ccd1]',
                )}
              >
                {formatNumber(position + 1, locale)}
              </span>
              <span className="min-w-0">
                <span
                  className="mb-1 block text-2xs font-extrabold uppercase tracking-wide"
                  style={{ color: article.category.color ?? 'var(--red)' }}
                >
                  {article.category.name}
                </span>
                <span
                  className={cn(
                    'block text-md font-bold transition group-hover:text-red',
                    active ? 'text-red' : 'text-ink',
                  )}
                >
                  {article.headline}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
